/* eslint-disable react/jsx-max-props-per-line */
import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import fileDownload from 'js-file-download';
import { Alert, Box, Button, CircularProgress, DialogContentText, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import { DialogDefaultV2, WidgetV2 } from '../../../../components/v2';
import PdfViewerDialogV2 from '../../../../components/v2/filePreview/PdfViewerDialogV2';
import ExcelPreviewPanel from '../../../../components/v2/filePreview/ExcelPreviewPanel';
import * as Status from '../../../../constants/status';
import * as actionsParseRfg from '../../../../store/parseRfg/actions';
import * as selectorsParseRfg from '../../../../store/parseRfg/reducer';
import * as actionsToast from '../../../../store/toast/actions';
import FileService from '../../../../services/FileService';
import { downloadPresignedUrl } from '../../../../helpers/downloadPresignedUrl';
import {
  canImportParseRfg,
  isAllowedParseRfgFile,
  isParseRfgAguardandoInspecaoConflict,
  isParseRfgExcelNome,
  isParseRfgJobOwnedByNsir,
  isParseRfgPdfNome,
  isParseRfgSemTextoExtraivel,
  PARSE_RFG_ACCEPT,
  PARSE_RFG_CONFLICT_AGUARDANDO_DEFAULT,
  PARSE_RFG_CONFLICT_DELETE_NSMP,
  PARSE_RFG_CONFLICT_REPLACE_DELETE_NSMP,
  PARSE_RFG_DEFAULT_NOME,
  PARSE_RFG_DOCUMENTO_PATH,
} from '../../../../helpers/auditoriaFaixa';
import BaseService from '../../../../services/BaseService';
import {
  RELATORIO_JOB_POLL_MS,
  RELATORIO_JOB_STATUS,
  getJobSubtitle,
  isRelatorioJobAvailable,
  isRelatorioJobInFlight,
} from '../../../../helpers/relatorioJob';

const ParseRfgPanel = ({
  nsirId,
  nsirStatus,
  canUpload,
  skipHydrate,
  requireFreshUpload,
  needsPersistNewFlow,
  onSaveThenUpload,
  rfgImportadoViaOcr,
  job,
  documento,
  uploading,
  conflict,
  startParseRfg,
  fetchParseRfgJob,
  fetchParseRfgDocumento,
  clearParseRfgConflict,
  messageError,
}) => {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [ocrConfirmOpen, setOcrConfirmOpen] = useState(false);
  const [saveThenUploadOpen, setSaveThenUploadOpen] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);
  const [previewNome, setPreviewNome] = useState('');
  const [fileBusy, setFileBusy] = useState(false);
  const fileInputRef = useRef(null);
  const lastFileRef = useRef(null);
  const lastForceRef = useRef(false);
  const ocrIntentRef = useRef(false);
  const ocrPromptedJobIdRef = useRef(null);
  const pickForOcrRef = useRef(false);

  const ownedJob = isParseRfgJobOwnedByNsir(job, nsirId) ? job : null;
  const success = isRelatorioJobAvailable(ownedJob && ownedJob.status);
  const busy = uploading || isRelatorioJobInFlight(ownedJob && ownedJob.status);
  const isAguardandoInspecao = nsirStatus === Status.AGUARDANDO_INSPECAO;
  const isAguardandoConflict = isParseRfgAguardandoInspecaoConflict(conflict, nsirStatus);
  const canMutate = canUpload && canImportParseRfg(nsirStatus) && !busy;
  const showFirstUpload = canMutate && !success;
  const showReplace = canMutate && success;
  const arquivoNome = (documento && documento.nome) || (ownedJob && ownedJob.nomeArquivo) || PARSE_RFG_DEFAULT_NOME;
  const arquivoUrl = (documento && documento.url) || (ownedJob && ownedJob.url) || null;
  const jobId = ownedJob && ownedJob.id;
  const displayNome = previewNome || arquivoNome;
  const isExcelArquivo = isParseRfgExcelNome(displayNome);
  const isPdfArquivo = isParseRfgPdfNome(displayNome);
  const semTexto = Boolean(
    ownedJob
    && ownedJob.status === RELATORIO_JOB_STATUS.ERROR
    && isParseRfgSemTextoExtraivel(ownedJob.error)
  );

  useEffect(() => {
    if (!nsirId || skipHydrate) return undefined;
    fetchParseRfgJob(nsirId, null);
    return undefined;
  }, [nsirId, skipHydrate, fetchParseRfgJob]);

  useEffect(() => {
    if (!nsirId || !ownedJob || !isRelatorioJobInFlight(ownedJob.status)) return undefined;
    const timer = window.setInterval(() => {
      fetchParseRfgJob(nsirId, ownedJob.id, { skipExisting: skipHydrate });
    }, RELATORIO_JOB_POLL_MS);
    return () => window.clearInterval(timer);
  }, [nsirId, ownedJob, skipHydrate, fetchParseRfgJob]);

  useEffect(() => {
    if (success && jobId) fetchParseRfgDocumento(jobId);
  }, [success, jobId, fetchParseRfgDocumento]);

  useEffect(() => {
    if (!conflict) return;
    if (!pendingFile && lastFileRef.current) setPendingFile(lastFileRef.current);
    setOcrConfirmOpen(false);
    setConfirmOpen(true);
  }, [conflict]);

  useEffect(() => {
    if (!semTexto || !ownedJob || !ownedJob.id) return;
    if (ocrPromptedJobIdRef.current === ownedJob.id) return;
    if (!lastFileRef.current) return;
    ocrPromptedJobIdRef.current = ownedJob.id;
    setPendingFile(lastFileRef.current);
    setOcrConfirmOpen(true);
  }, [semTexto, ownedJob]);

  const beginUpload = (file, force, ocr) => {
    lastFileRef.current = file;
    lastForceRef.current = Boolean(force);
    ocrIntentRef.current = Boolean(ocr);
    setPendingFile(file);
    startParseRfg(nsirId, file, force, ocr);
  };

  const enqueueFile = (file) => {
    if (!file) return;
    if (!isAllowedParseRfgFile(file)) {
      messageError({ message: 'Envie um PDF ou Excel (.xls, .xlsx).' });
      return;
    }
    lastFileRef.current = file;
    lastForceRef.current = false;
    ocrIntentRef.current = false;
    setPendingFile(file);
    if (needsPersistNewFlow && onSaveThenUpload) {
      setSaveThenUploadOpen(true);
      return;
    }
    beginUpload(file, false, false);
  };

  const onPickFile = (event) => {
    const file = event.target.files && event.target.files[0];
    const forOcr = pickForOcrRef.current;
    pickForOcrRef.current = false;
    event.target.value = '';
    if (!file) return;
    if (forOcr) {
      if (!isParseRfgPdfNome(file.name)) {
        messageError({ message: 'O reconhecimento óptico vale só para PDF.' });
        return;
      }
      lastFileRef.current = file;
      setPendingFile(file);
      setOcrConfirmOpen(true);
      return;
    }
    enqueueFile(file);
  };

  const requestOcr = () => {
    const file = lastFileRef.current;
    if (file) {
      if (!isParseRfgPdfNome(file.name)) {
        messageError({ message: 'O reconhecimento óptico vale só para PDF.' });
        return;
      }
      setPendingFile(file);
      setOcrConfirmOpen(true);
      return;
    }
    pickForOcrRef.current = true;
    if (fileInputRef.current) fileInputRef.current.click();
  };

  const resolveArquivo = async () => {
    if (arquivoUrl) return { url: arquivoUrl, nome: arquivoNome };
    if (!jobId) return null;
    try {
      const response = await BaseService.get({ url: PARSE_RFG_DOCUMENTO_PATH(jobId) });
      const data = response.data || {};
      const url = data.url || data.arquivoUrl || (data.arquivo && data.arquivo.url);
      const nome = data.nome || data.nomeArquivo || (data.arquivo && data.arquivo.nome) || arquivoNome;
      if (url) return { url, nome };
    } catch (error) {
      // job ainda PROCESSING ou arquivo indisponível
    }
    return null;
  };

  const loadArquivoBlob = async (url) => {
    const response = await FileService.getFile(url);
    return response.data;
  };

  const toPreviewFile = (blob, nome) => {
    if (blob instanceof File) return blob;
    return new File([blob], nome || PARSE_RFG_DEFAULT_NOME, { type: blob.type || '' });
  };

  const handleView = async () => {
    setFileBusy(true);
    try {
      const resolved = await resolveArquivo();
      if (!resolved) {
        messageError({ message: 'O arquivo do RFG ainda não está disponível para visualização neste ambiente.' });
        return;
      }
      const blob = await loadArquivoBlob(resolved.url);
      setPreviewNome(resolved.nome);
      if (!isParseRfgPdfNome(resolved.nome) && !isParseRfgExcelNome(resolved.nome)) {
        downloadPresignedUrl(resolved.url);
        return;
      }
      setPreviewFile(toPreviewFile(blob, resolved.nome));
      setPreviewOpen(true);
    } catch (error) {
      const resolved = await resolveArquivo();
      if (resolved) downloadPresignedUrl(resolved.url);
      else messageError({ message: 'Não foi possível abrir o arquivo do RFG.' });
    } finally {
      setFileBusy(false);
    }
  };

  const handleDownload = async () => {
    setFileBusy(true);
    try {
      const resolved = await resolveArquivo();
      if (!resolved) {
        messageError({ message: 'O arquivo do RFG ainda não está disponível para download neste ambiente.' });
        return;
      }
      try {
        const blob = await loadArquivoBlob(resolved.url);
        fileDownload(blob, resolved.nome);
      } catch (error) {
        downloadPresignedUrl(resolved.url);
      }
    } finally {
      setFileBusy(false);
    }
  };

  const severity = () => {
    if (!ownedJob) return 'info';
    if (isRelatorioJobInFlight(ownedJob.status)) return 'info';
    if (ownedJob.status === RELATORIO_JOB_STATUS.ERROR) return semTexto ? 'warning' : 'error';
    if (success) return 'success';
    return 'info';
  };

  let FileIcon = InsertDriveFileOutlinedIcon;
  if (isExcelArquivo) FileIcon = TableChartOutlinedIcon;
  else if (isPdfArquivo) FileIcon = PictureAsPdfOutlinedIcon;
  const headerAvatar = (busy || fileBusy) ? (
    <CircularProgress size={18} thickness={5} sx={{ color: 'common.white' }} />
  ) : (
    <FileIcon sx={{ fontSize: 22 }} />
  );

  return (
    <WidgetV2 title="Relatório fotográfico (RFG)" insideTab avatar={headerAvatar}>
      <Box sx={{ px: 1.5, py: 1 }}>
        {ownedJob && (
          <Alert
            severity={severity()}
            sx={{ mb: 1, py: 0, '& .MuiAlert-message': { py: 0.75, fontSize: 13 } }}
          >
            {semTexto
              ? 'Este PDF não tem texto extraível. Confirme o reconhecimento óptico (OCR) para processar mesmo assim.'
              : getJobSubtitle(ownedJob)}
          </Alert>
        )}
        {rfgImportadoViaOcr && (
          <Alert
            severity="warning"
            sx={{ mb: 1, py: 0, '& .MuiAlert-message': { py: 0.75, fontSize: 13 } }}
          >
            RFG importado por reconhecimento óptico. Revise coordenadas e quantidades antes de liberar a inspeção.
          </Alert>
        )}
        {requireFreshUpload && !success && (
          <Typography variant="caption" color="warning.main" display="block" sx={{ mb: 1 }}>
            {needsPersistNewFlow
              ? 'Esta inspeção estava no fluxo antigo. Ao enviar o arquivo, salvaremos o Novo Fluxo = Sim e em seguida processaremos o RFG.'
              : 'Esta inspeção estava no fluxo antigo. Envie um PDF ou Excel novo — o último RFG não é reaproveitado.'}
          </Typography>
        )}
        {!success && (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
            Envie o PDF ou Excel da terceirizada. O parse pré-preenche a aba Serviços; se falhar, publique a inspeção e cadastre os pontos na mão.
          </Typography>
        )}
        <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
          {success && (
            <>
              <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
                {arquivoNome}
              </Typography>
              <Tooltip title="Visualizar arquivo">
                <span>
                  <IconButton size="small" onClick={handleView} disabled={fileBusy} aria-label="Visualizar arquivo">
                    <VisibilityOutlinedIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Baixar arquivo">
                <span>
                  <IconButton size="small" onClick={handleDownload} disabled={fileBusy} aria-label="Baixar arquivo">
                    <DownloadOutlinedIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </>
          )}
          {(showFirstUpload || showReplace) && (
            <>
              <input
                ref={fileInputRef}
                type="file"
                accept={PARSE_RFG_ACCEPT}
                hidden
                onChange={onPickFile}
              />
              {semTexto && (
                <Button
                  size="small"
                  variant="contained"
                  onClick={requestOcr}
                  disabled={!canMutate}
                  sx={{ fontWeight: 600, minWidth: 'auto', px: 1.25 }}
                >
                  Processar com OCR
                </Button>
              )}
              <Button
                size="small"
                variant={showReplace || semTexto ? 'outlined' : 'contained'}
                onClick={() => fileInputRef.current && fileInputRef.current.click()}
                disabled={!canMutate}
                sx={{ fontWeight: 600, minWidth: 'auto', px: 1.25 }}
              >
                {showReplace ? 'Substituir' : 'Enviar arquivo'}
              </Button>
            </>
          )}
        </Stack>
      </Box>
      <DialogDefaultV2
        open={saveThenUploadOpen}
        title="Salvar e processar RFG?"
        confirmMessage="Salvar e enviar"
        handlerCancel={() => {
          setSaveThenUploadOpen(false);
          setPendingFile(null);
        }}
        handlerConfirm={() => {
          const file = pendingFile || lastFileRef.current;
          setSaveThenUploadOpen(false);
          setPendingFile(null);
          if (file && onSaveThenUpload) onSaveThenUpload(file);
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <WarningAmberOutlinedIcon color="warning" sx={{ fontSize: 28, flexShrink: 0 }} />
          <DialogContentText>
            O Novo Fluxo ainda não está salvo nesta inspeção. Vamos salvar com Novo Fluxo = Sim
            e em seguida processar o arquivo selecionado.
          </DialogContentText>
        </Stack>
      </DialogDefaultV2>
      <DialogDefaultV2
        open={confirmOpen}
        title={isAguardandoConflict ? 'NSIR aguardando inspeção' : 'Substituir o RFG?'}
        confirmMessage={isAguardandoConflict ? 'Já alinhei, continuar' : 'Substituir'}
        handlerCancel={() => {
          setConfirmOpen(false);
          setPendingFile(null);
          if (conflict) clearParseRfgConflict();
        }}
        handlerConfirm={() => {
          const file = pendingFile || lastFileRef.current;
          const shouldForce = Boolean(conflict);
          setConfirmOpen(false);
          setPendingFile(null);
          if (conflict) clearParseRfgConflict();
          if (file && shouldForce) beginUpload(file, true, ocrIntentRef.current);
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <WarningAmberOutlinedIcon color="warning" sx={{ fontSize: 28, flexShrink: 0 }} />
          <DialogContentText>
            {isAguardandoConflict
              ? (isParseRfgAguardandoInspecaoConflict(conflict, null)
                ? conflict
                : PARSE_RFG_CONFLICT_AGUARDANDO_DEFAULT)
              : (typeof conflict === 'string' ? conflict : 'Já existe um RFG importado para esta NSIR. Deseja substituir?')}
            {' '}
            {isAguardandoConflict
              ? PARSE_RFG_CONFLICT_DELETE_NSMP
              : PARSE_RFG_CONFLICT_REPLACE_DELETE_NSMP}
          </DialogContentText>
        </Stack>
      </DialogDefaultV2>
      <DialogDefaultV2
        open={ocrConfirmOpen}
        title="Processar com OCR?"
        confirmMessage="Processar com OCR"
        handlerCancel={() => {
          setOcrConfirmOpen(false);
          setPendingFile(null);
        }}
        handlerConfirm={() => {
          const file = pendingFile || lastFileRef.current;
          setOcrConfirmOpen(false);
          if (file) beginUpload(file, lastForceRef.current, true);
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <WarningAmberOutlinedIcon color="warning" sx={{ fontSize: 28, flexShrink: 0 }} />
          <DialogContentText>
            Este PDF não tem texto extraível. O reconhecimento óptico pode errar dígitos de
            coordenada e quantidade. Confirme para processar este arquivo mesmo assim.
          </DialogContentText>
        </Stack>
      </DialogDefaultV2>
      <PdfViewerDialogV2
        open={previewOpen && isPdfArquivo}
        onClose={() => setPreviewOpen(false)}
        file={previewFile}
        title={displayNome}
      />
      <DialogDefaultV2
        open={previewOpen && isExcelArquivo}
        title={displayNome}
        readOnly
        maxWidth="md"
        fullWidth
        handlerCancel={() => setPreviewOpen(false)}
        handlerConfirm={() => setPreviewOpen(false)}
      >
        <ExcelPreviewPanel file={previewFile} />
      </DialogDefaultV2>
    </WidgetV2>
  );
};

ParseRfgPanel.propTypes = {
  nsirId: PropTypes.string,
  nsirStatus: PropTypes.string,
  canUpload: PropTypes.bool,
  skipHydrate: PropTypes.bool,
  requireFreshUpload: PropTypes.bool,
  needsPersistNewFlow: PropTypes.bool,
  onSaveThenUpload: PropTypes.func,
  rfgImportadoViaOcr: PropTypes.bool,
  job: PropTypes.object,
  documento: PropTypes.object,
  uploading: PropTypes.bool,
  conflict: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  startParseRfg: PropTypes.func.isRequired,
  fetchParseRfgJob: PropTypes.func.isRequired,
  fetchParseRfgDocumento: PropTypes.func.isRequired,
  clearParseRfgConflict: PropTypes.func.isRequired,
  messageError: PropTypes.func.isRequired,
};

ParseRfgPanel.defaultProps = {
  nsirId: null,
  nsirStatus: null,
  canUpload: false,
  skipHydrate: false,
  requireFreshUpload: false,
  needsPersistNewFlow: false,
  onSaveThenUpload: null,
  rfgImportadoViaOcr: false,
  job: null,
  documento: null,
  uploading: false,
  conflict: null,
};

const mapStateToProps = (state) => ({
  job: selectorsParseRfg.getParseRfgJob(state),
  uploading: selectorsParseRfg.getParseRfgUploading(state),
  documento: selectorsParseRfg.getParseRfgDocumento(state),
  conflict: selectorsParseRfg.getParseRfgConflict(state),
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators({ ...actionsParseRfg, ...actionsToast }, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(ParseRfgPanel);
